import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const REPORT_TYPES = [
  { value: 'bug', label: 'Bug Report', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', activeBg: 'bg-red-500/20 border-red-500/60' },
  { value: 'suggestion', label: 'Suggestion', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z', color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30', activeBg: 'bg-yellow-500/20 border-yellow-500/60' },
  { value: 'feature', label: 'Feature Request', icon: 'M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30', activeBg: 'bg-blue-500/20 border-blue-500/60' },
];

export default function BugReportModal({ isOpen, onClose }) {
  const { user, profile } = useAuth();
  const location = useLocation();
  const [type, setType] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setType('bug');
      setTitle('');
      setDescription('');
      setSubmitting(false);
      setDone(false);
      setError('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      setError('Please fill in both the title and description.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const { supabase } = await import('../../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch('/api/bug-reports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          type,
          title: title.trim(),
          description: description.trim(),
          page_url: location.pathname,
          reporter_name: profile?.full_name || user?.email || 'Unknown',
          reporter_role: profile?.role || 'unknown',
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to submit');
      }
      setDone(true);
      setTimeout(() => { onClose(); }, 2200);
    } catch (e) {
      setError(e.message || 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedType = REPORT_TYPES.find(t => t.value === type);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-theme shadow-2xl overflow-hidden"
        style={{ background: 'var(--color-card, #1a1a2e)' }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-theme">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
              <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
            </div>
            <div>
              <h2 className="text-sm font-bold text-primary">Report or Suggest</h2>
              <p className="text-[11px] text-muted">Help us improve the platform</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted hover:text-primary min-w-[32px] min-h-[32px] flex items-center justify-center rounded-lg hover:bg-primary/5 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {done ? (
          <div className="px-6 py-12 flex flex-col items-center text-center gap-4">
            <div className="w-14 h-14 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center">
              <svg className="w-7 h-7 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-base font-bold text-primary">Thanks for the report!</p>
              <p className="text-sm text-muted mt-1">The team will review it shortly.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted mb-2">Type</label>
              <div className="grid grid-cols-3 gap-2">
                {REPORT_TYPES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    className={`flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-xl border text-center transition-all duration-200 ${
                      type === t.value ? t.activeBg : t.bg + ' hover:opacity-80'
                    }`}
                  >
                    <svg className={`w-4 h-4 ${t.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={type === t.value ? 2 : 1.5} d={t.icon} />
                    </svg>
                    <span className={`text-[11px] font-semibold ${type === t.value ? t.color : 'text-muted'}`}>{t.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted mb-1.5">
                Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={type === 'bug' ? 'Brief description of the issue...' : type === 'suggestion' ? 'What would you improve?' : 'What feature would help you?'}
                maxLength={120}
                className="w-full px-3 py-2.5 rounded-xl border border-theme bg-primary/5 text-primary text-sm placeholder-muted focus:outline-none focus:border-yellow-500/50 transition-colors"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted mb-1.5">
                Details <span className="text-red-400">*</span>
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={type === 'bug' ? 'Steps to reproduce, what you expected, what happened...' : 'Describe your idea or suggestion in detail...'}
                rows={4}
                maxLength={1000}
                className="w-full px-3 py-2.5 rounded-xl border border-theme bg-primary/5 text-primary text-sm placeholder-muted focus:outline-none focus:border-yellow-500/50 transition-colors resize-none"
              />
              <div className="text-right text-[10px] text-muted mt-1">{description.length}/1000</div>
            </div>

            {error && (
              <div className="px-3 py-2.5 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-400">
                {error}
              </div>
            )}

            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-xl border border-theme text-secondary text-sm font-medium hover:text-primary hover:border-primary/30 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !title.trim() || !description.trim()}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #C9A84C, #A8863C)',
                  color: '#000',
                  boxShadow: submitting ? 'none' : '0 2px 12px rgba(201,168,76,0.3)',
                }}
              >
                {submitting ? 'Sending...' : 'Submit Report'}
              </button>
            </div>

            <div className="flex items-center gap-1.5 justify-center">
              <svg className="w-3 h-3 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-[10px] text-muted">Page: {location.pathname}</span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
